import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, Row, Select } from '../components/form';
import { Badge, Button, Card, Input } from '../components/ui';
import { api } from '../lib/api';

interface AuditRow {
  id: string;
  actorType: 'user' | 'api' | 'system' | 'extension';
  actorId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  at: string;
}

interface Page {
  data: AuditRow[];
  nextCursor: string | null;
}

/** Actions worth singling out, because they are the ones an auditor comes looking for. */
const SENSITIVE = new Set(['monitor.start', 'monitor.mode', 'monitor.barge']);

/**
 * The operation log.
 *
 * Read-only by design, and there is no delete anywhere in the API: a trail an administrator can
 * edit is not a trail. Entries age out through the worker's retention pass instead.
 */
export function OperationLog() {
  const [action, setAction] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [stack, setStack] = useState<string[]>([]);

  const { data } = useQuery({
    queryKey: ['audit', action, cursor],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '50' });
      if (action) params.set('action', action);
      if (cursor) params.set('cursor', cursor);
      return api.get<Page>(`/api/v1/audit?${params}`);
    },
  });

  const rows = data?.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operation log"
        description="Who did what, and from where. Cannot be edited or deleted from here."
      />

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-52">
            <Row label="Action" hint="Leave blank for everything.">
              <Input
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  setCursor(null);
                  setStack([]);
                }}
                placeholder="monitor.start"
              />
            </Row>
          </div>
        </div>
      </Card>

      <div className="overflow-x-auto rounded-[--radius] border border-border bg-card">
        <table className="w-full min-w-[52rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left">
              {['Time', 'Who', 'Action', 'Details', 'From'].map((header) => (
                <th
                  key={header}
                  className="whitespace-nowrap px-3 py-2.5 font-medium text-muted-foreground"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-12 text-center text-muted-foreground">
                  Nothing logged yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0">
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums">
                    {new Date(row.at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5">
                    {/* The actor's label is stored on the entry, so a deleted admin still reads
                        correctly here — which is exactly the entry worth reading. */}
                    {(row.after?.['actor'] as string) ?? row.actorType}
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge tone={SENSITIVE.has(row.action) ? 'warning' : 'muted'}>
                      {row.action}
                    </Badge>
                  </td>
                  <td className="max-w-md px-3 py-2.5 font-mono text-xs text-muted-foreground">
                    {summarise(row)}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">
                    {row.ip ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          disabled={stack.length === 0}
          onClick={() => {
            const previous = [...stack];
            const back = previous.pop() ?? null;
            setStack(previous);
            setCursor(back);
          }}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          disabled={!data?.nextCursor}
          onClick={() => {
            if (!data?.nextCursor) return;
            setStack((s) => [...s, cursor ?? '']);
            setCursor(data.nextCursor);
          }}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

/** Everything except the actor label, which already has its own column. */
function summarise(row: AuditRow): string {
  const { actor: _actor, ...rest } = (row.after ?? {}) as Record<string, unknown>;
  const parts = Object.entries(rest).map(([key, value]) => `${key}=${String(value)}`);
  return parts.length > 0 ? parts.join(' ') : row.entityType;
}
